import styled, { keyframes } from "styled-components";

const slideIn = keyframes`
  from { transform: translateX(100%); }
  to   { transform: translateX(0); }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.3);
  z-index: 300;
  display: flex;
  justify-content: flex-end;
`;

export const Panel = styled.div`
  width: 560px;
  height: 100%;
  background-color: #fff;
  display: flex;
  flex-direction: column;
  animation: ${slideIn} 0.22s ease;
  box-shadow: -4px 0 32px rgba(0, 0, 0, 0.12);
`;

export const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 24px 28px;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
`;

export const PanelTitle = styled.h2`
  font-size: 18px;
  font-weight: 800;
  color: #111;
  letter-spacing: -0.02em;
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const EditButton = styled.button`
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background-color: #1fbc70;
  color: #fff;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover { background-color: #18a360; }
`;

export const DeleteButton = styled.button`
  height: 32px;
  padding: 0 14px;
  border: none;
  border-radius: 8px;
  background-color: #fff0f0;
  color: #fc324a;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover { background-color: #ffdde0; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: none;
  background-color: #f5f5f5;
  font-size: 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #555;
  transition: background-color 0.15s;
  flex-shrink: 0;

  &:hover { background-color: #ebebeb; }
`;

export const PanelBody = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 28px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const NoticeTitle = styled.h3`
  font-size: 18px;
  font-weight: 700;
  color: #111;
  line-height: 1.5;
  letter-spacing: -0.01em;
`;

export const MetaRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 20px;
  border-bottom: 1px solid #f0f0f0;
`;

export const TypeBadge = styled.span<{ type?: string }>`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 700;
  background-color: ${({ type }) => (type === "IMPORTANT" ? "#fff0f0" : "#f0fdf7")};
  color: ${({ type }) => (type === "IMPORTANT" ? "#fc324a" : "#1fbc70")};
  border: 1px solid ${({ type }) => (type === "IMPORTANT" ? "#ffd0d5" : "#d0f0e0")};
`;

export const DateText = styled.span`
  font-size: 13px;
  color: #aaa;
`;

export const ContentBox = styled.div`
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  word-break: break-word;

  img { max-width: 100%; height: auto; border-radius: 8px; }
  a { color: #1fbc70; text-decoration: underline; }
  p { margin: 0 0 8px; }
  ul, ol { padding-left: 20px; margin: 0 0 8px; }
  table { border-collapse: collapse; width: 100%; }
  td, th { border: 1px solid #e0e0e0; padding: 6px 10px; font-size: 13px; }
`;

export const LoadingText = styled.p`
  text-align: center;
  font-size: 14px;
  color: #bbb;
  padding: 60px 0;
`;

/* ── 탭 ── */

export const TabRow = styled.div`
  display: flex;
  gap: 0;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
`;

export const TabButton = styled.button<{ active?: boolean }>`
  flex: 1;
  height: 32px;
  border: none;
  background-color: ${({ active }) => (active ? "#1fbc70" : "#fff")};
  color: ${({ active }) => (active ? "#fff" : "#888")};
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s, color 0.15s;

  &:hover:not([data-active="true"]) { background-color: #f5f5f5; }
`;

export const PreviewBox = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 14px;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  font-size: 14px;
  color: #333;
  line-height: 1.8;
  word-break: break-word;
  min-height: 240px;
  background-color: #fafafa;

  img { max-width: 100%; height: auto; border-radius: 8px; }
  a { color: #1fbc70; text-decoration: underline; }
  p { margin: 0 0 8px; }
  ul, ol { padding-left: 20px; margin: 0 0 8px; }
  table { border-collapse: collapse; width: 100%; }
  td, th { border: 1px solid #e0e0e0; padding: 6px 10px; font-size: 13px; }
`;

/* ── 수정 폼 ── */

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const FormLabel = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: #666;
`;

export const FormInput = styled.input`
  height: 36px;
  padding: 0 12px;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  font-size: 13px;
  color: #111;
  background-color: #fff;
  box-sizing: border-box;
  width: 100%;
  transition: border-color 0.15s;

  &::placeholder { color: #bbb; }
  &:focus { outline: none; border-color: #1fbc70; }
  &:disabled { opacity: 0.6; }
`;

export const FormSelect = styled.select`
  height: 36px;
  padding: 0 12px;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  font-size: 13px;
  color: #111;
  background-color: #fff;
  width: 100%;
  cursor: pointer;
  transition: border-color 0.15s;

  &:focus { outline: none; border-color: #1fbc70; }
  &:disabled { opacity: 0.6; }
`;

export const FormTextarea = styled.textarea`
  padding: 10px 12px;
  border: 1.5px solid #e8e8e8;
  border-radius: 8px;
  font-size: 13px;
  color: #111;
  background-color: #fff;
  box-sizing: border-box;
  width: 100%;
  resize: vertical;
  line-height: 1.6;
  font-family: monospace;
  transition: border-color 0.15s;

  &::placeholder { color: #bbb; }
  &:focus { outline: none; border-color: #1fbc70; }
  &:disabled { opacity: 0.6; }
`;

export const FormActions = styled.div`
  display: flex;
  gap: 10px;
  padding-top: 8px;
  border-top: 1px solid #f0f0f0;
  margin-top: auto;
`;

export const SaveButton = styled.button`
  flex: 1;
  height: 42px;
  border: none;
  border-radius: 10px;
  background-color: #1fbc70;
  color: #fff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover:not(:disabled) { background-color: #18a360; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;

export const CancelButton = styled.button`
  flex: 1;
  height: 42px;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  background-color: #fff;
  color: #555;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.15s;

  &:hover:not(:disabled) { background-color: #f5f5f5; }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
`;
